import React, { useState, useEffect } from "react";
import { Input } from "reactstrap";
import Select from "react-select";
import axios from "axios";
import configure from "configure";

const fmt = (n, symbol = "£") =>
  `${symbol} ${Number(n || 0).toLocaleString("en-GB", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;

const timingKey = (sid, fi, si, role) => `${sid}|${fi}|${si}|${role}`;
const grandRoleHours = (timing, features, role) =>
  (features || []).reduce((acc, svc) =>
    acc + svc.subtitles.reduce((a2, sub, fi) =>
      a2 + Array.from({ length: sub.features.length }, (_, si) =>
        timing[timingKey(svc.serviceId, fi, si, role)] || 0).reduce((x, y) => x + y, 0), 0), 0);

const StepCosting = ({
  features, timing, services, pricingMode,
  costingCurrency, setCostingCurrency,
  devTechOption, setDevTechOption,
  baTechOption, setBaTechOption,
  qaTechOption, setQaTechOption,
  overheadTotal, setOverheadTotal,
  commissionStaff, setCommissionStaff,
  commissionPct, setCommissionPct,
  profitPct, setProfitPct,
  goCardlessEnabled, setGoCardlessEnabled,
  gstPct, setGstPct,
  goCardlessPct, setGoCardlessPct,
  readOnly, stepLogs, allUsers,
}) => {
  const [allCurrency, setAllCurrency] = useState([]);
  const [currency, setCurrency] = useState(costingCurrency || { label: "INR", value: "INR", symbol: "₹" });
  const [isLoading, setIsLoading] = useState(false);
  const [techRateData, setTechRateData] = useState([]);
  const [overheadRawData, setOverheadRawData] = useState([]);

  const staffOptions = (allUsers || []).map(u => ({ label: u.name, value: u.id }));

  const staffcount = (allUsers || []).filter(item => item?.overhead === 1).length

  useEffect(() => { if (costingCurrency?.value) { setCurrency(costingCurrency) } }, [costingCurrency]);

  // Recalculate overhead total whenever the selected currency or raw data changes
  useEffect(() => {
    if (!overheadRawData.length) return;
    const key = (currency?.value || "GBP").toLowerCase();
    const total = Math.ceil(overheadRawData.reduce((s, oh) => s + (Number(oh[key]) || 0), 0));
    setOverheadTotal?.(total);
  }, [currency, overheadRawData]);

  const rowToOption = (row) => ({
    label: `${row.Technology}${row.status !== "optional" ? ` (${row.status})` : ""}`,
    value: row.Technology,
    rateRow: row,
  });

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        // Currencies
        const currRes = await axios.get(`${configure.apiUrl}/currency`, { headers: { Accept: "application/json" } });
        const opts = currRes.data.data.currency.map(c => ({ label: c.currency, value: c.currency, symbol: c.symbol }));
        setAllCurrency(opts);
        const gbp = opts.find(c => c.value === "INR") || opts[0];
        if (!costingCurrency?.value && gbp) {
          setCurrency(gbp);
          setCostingCurrency?.(gbp);
        } else if (costingCurrency?.value) {
          const saved = opts.find(c => c.value === costingCurrency.value);
          if (saved) setCurrency(saved);
        }

        // Technology rates
        const techRes = await axios.get(`${configure.nodeApi}/show_technologies`, { headers: { Accept: "application/json" } });
        let rows = [];
        if (techRes.data?.data?.length > 0) {
          const first = techRes.data.data[0];
          if (typeof first.currency_value === "string") rows = JSON.parse(first.currency_value);
          else if (Array.isArray(first.currency_value)) rows = first.currency_value;
          else rows = techRes.data.data;
        }
        rows = rows.map(r => ({ status: "optional", ...r }));
        setTechRateData(rows);

        // Restore from saved (hydration) OR pick default per role
        const restoreOrDefault = (currentOpt, defaultStatus, setter) => {
          if (currentOpt?.value && !currentOpt.rateRow) {
            const match = rows.find(r => r.Technology === currentOpt.value);
            if (match) setter(rowToOption(match));
          } else if (!currentOpt) {
            const row = rows.find(r => r.status === defaultStatus);
            if (row) setter(rowToOption(row));
          }
        };
        restoreOrDefault(devTechOption, "default", setDevTechOption);
        restoreOrDefault(baTechOption, "ba", setBaTechOption);
        restoreOrDefault(qaTechOption, "qa", setQaTechOption);

        // Overheads — store raw rows; totaling is done per-currency in the effect below
        const ohRes = await axios.get(`${configure.nodeApi}/overheads`, { headers: { Accept: "application/json" } });
        if (ohRes.data?.success && Array.isArray(ohRes.data.data)) {
          setOverheadRawData(ohRes.data.data);
        }
      } catch (e) {
        console.error("StepCosting init failed:", e.message);
      } finally {
        setIsLoading(false);
      }
    };
    init();
  }, []);

  // Restore commissionStaff label when allUsers becomes available
  useEffect(() => {
    if (commissionStaff?.value && commissionStaff.label?.startsWith("Staff #") && allUsers?.length > 0) {
      const found = allUsers.find(u => u.id == commissionStaff.value);
      if (found) setCommissionStaff({ value: found.id, label: found.name });
    }
  }, [allUsers]);

  const currValue = currency?.value || "INR";
  const currSymbol = currency?.symbol || "₹";
  const allTechOptions = techRateData.map(rowToOption);
  const rateOf = (opt) => (opt?.rateRow ? Number(opt.rateRow[currValue]) || 0 : 0);

  // Hours — from timing prop
  const devHours = grandRoleHours(timing, features, "dev");
  const baHours = grandRoleHours(timing, features, "ba");
  const qaHours = grandRoleHours(timing, features, "qa");
  const totalHours = devHours + baHours + qaHours;

  // Per-role costs
  const devRate = rateOf(devTechOption);
  const baRate = rateOf(baTechOption);
  const qaRate = rateOf(qaTechOption);
  const devCost = devHours * devRate;
  const baCost = baHours * baRate;
  const qaCost = qaHours * qaRate;

  // Overhead applied to total hours
  const overheadCost = totalHours * Math.ceil(Number(overheadTotal) / Number(staffcount) || 0);

  // Base cost = sum
  const baseCost = devCost + baCost + qaCost + overheadCost;

  // Commission + GoCardless (NO VAT)
  // const commissionAmount = baseCost * ((Number(commissionPct) || 0) / 100);
  // const afterCommission  = baseCost + commissionAmount;
  // const goCardlessAmount = goCardlessEnabled
  //   ? afterCommission * ((Number(goCardlessPct) || 0) / 100)
  //   : 0;
  // const grandTotal = afterCommission + goCardlessAmount;
  const profitAmount = baseCost * ((Number(profitPct) || 0) / 100);
  const afterProfit = baseCost + profitAmount;
  const commissionAmount = afterProfit * ((Number(commissionPct) || 0) / 100);
  const afterCommission = afterProfit + commissionAmount;
  const goCardlessAmount = goCardlessEnabled
    ? afterCommission * ((Number(goCardlessPct) || 0) / 100)
    : 0;
  const subTotal = afterCommission + goCardlessAmount;
  const gstAmount = goCardlessEnabled
    ? subTotal * ((Number(gstPct) || 0) / 100)
    : 0;
  const grandTotal = subTotal + gstAmount;

  const makeSelectStyles = (minWidth = "100px", width = "160px") => ({
    control: (b) => ({ ...b, minHeight: "28px", height: "28px", fontSize: "11px", borderColor: "#d8bfe8", boxShadow: "none", flexWrap: "nowrap", minWidth, width, "&:hover": { borderColor: "#752a97" } }),
    valueContainer: (b) => ({ ...b, height: "28px", padding: "0 6px", flexWrap: "nowrap", overflow: "hidden" }),
    singleValue: (b) => ({ ...b, fontSize: "11px", color: "#330148", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }),

    indicatorsContainer: (b) => ({ ...b, height: "28px" }),
    dropdownIndicator: (b) => ({ ...b, padding: "0 4px" }),
    clearIndicator: (b) => ({ ...b, padding: "0 2px" }),
    menu: (b) => ({ ...b, fontSize: "13px" }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
    menuList: (b) => ({ ...b, maxHeight: "200px", overflowY: "auto" }),
    option: (b, { isFocused, isSelected }) => ({
      ...b,
      background: isSelected ? "#752a97" : isFocused ? "#f0e6f6" : "#fff",
      color: isSelected ? "#fff" : "#330148", fontSize: "12px", padding: "6px 12px",
    }),
  });

  // ── Role row renderer ──────────────────────────────────────────────────────
  const renderRoleRow = (label, hours, techOpt, setTechOpt, rate, cost) => (
    <div className="rfp-hours-row rfp-cost-row--v2">
      <div className="rfp-hours-row-label">
        <span className="rfp-cost-col-role">{label}</span>
      </div>
      <div className="rfp-cost-hrs-cell">
        <span className="rfp-cost-hrs-value">{hours} hrs</span>
      </div>
      <div>
        <Select
          options={allTechOptions}
          value={techOpt}
          onChange={setTechOpt}
          styles={makeSelectStyles("160px", "180px")}
          placeholder="Select technology…"
          isDisabled={isLoading || readOnly}
          menuPortalTarget={document.body}
          menuPosition="fixed"
        />
      </div>
      <div className="rfp-cost-rate-cell">
        {rate > 0
          ? <span className="rfp-cost-rate-display">{currSymbol}&nbsp;{rate.toFixed(2)}</span>
          : <em className="text-muted" style={{ fontSize: "12px" }}>—</em>}
      </div>
      <div style={{ textAlign: "right" }}>
        <span className="rfp-cost-total-value">{fmt(cost, currSymbol)}</span>
      </div>
    </div>
  );

  return (
    <div className="rfp-features">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="rfp-title mb-0">Project Costing</h4>
        <select
          className="form-select rfp-currency-select"
          style={{ width: "80px", height: "40px", textAlign: "center", fontSize: "13px" }}
          value={currValue}
          onChange={(e) => {
            const found = allCurrency.find(c => c.value === e.target.value);
            if (found) { setCurrency(found); setCostingCurrency?.(found); }
          }}
          disabled={isLoading || readOnly}
        >
          {allCurrency.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>

      <div className="rfp-hours-summary">
        {/* Column header */}
        <div className="rfp-cost-col-header rfp-cost-col-header--v2">
          <span>Role</span>
          <span style={{ textAlign: "center" }}>Hours</span>
          <span>Technology</span>
          <span>Rate / hr</span>
          <span style={{ textAlign: "right" }}>Total Cost</span>
        </div>

        {/* THREE ROLE ROWS */}
        {renderRoleRow("Developer", devHours, devTechOption, setDevTechOption, devRate, devCost)}
        {renderRoleRow("BA", baHours, baTechOption, setBaTechOption, baRate, baCost)}
        {renderRoleRow("QA", qaHours, qaTechOption, setQaTechOption, qaRate, qaCost)}

        {/* OVERHEAD ROW */}
        <div className="rfp-hours-row rfp-cost-row--v2">
          <div className="rfp-hours-row-label">
            <span className="rfp-cost-col-role">Overhead</span>
            <span className="rfp-hours-auto-tag">auto-calculated</span>
          </div>
          <div className="rfp-cost-hrs-cell">
            <span className="rfp-cost-hrs-value">{totalHours} hrs</span>
          </div>
          <div>
            <em className="text-muted" style={{ fontSize: "11px" }}>flat overhead</em>
          </div>
          <div className="rfp-cost-rate-cell">
            <span className="rfp-cost-rate-display">
              {currSymbol}&nbsp;{Math.ceil(Number(overheadTotal) / Number(staffcount) || 0)}
            </span>
          </div>
          <div style={{ textAlign: "right" }}>
            <span className="rfp-cost-total-value">{fmt(overheadCost, currSymbol)}</span>
          </div>
        </div>

        {/* BASE COST */}
        <div className="rfp-hours-row rfp-subtotal-row">
          <span className="rfp-cost-col-role fw-semibold">Base Cost</span>
          <div className="rfp-subtotal-right">
            <span className="rfp-subtotal-value">{fmt(baseCost, currSymbol)}</span>
          </div>
        </div>

        {/* PROFIT */}
        <div className="rfp-hours-row rfp-charge-row" style={{ gap: "40px" }}>
          <div className="rfp-cost-col-role rfp-hours-row-label">Profit</div>
          <div className="rfp-charge-row-controls" style={{ gridColumn: "3 / 5" }}>
            <div className="rfp-pct-wrap">
              <Input
                type="number" min={0} max={100} placeholder="0"
                value={profitPct}
                onChange={(e) => !readOnly && setProfitPct(e.target.value)}
                onKeyPress={(e) => { if (!/[0-9.]/.test(e.key)) e.preventDefault(); }}
                disabled={readOnly}
                className="rfp-pct-input"
              />
              <span className="rfp-pct-symbol">%</span>
            </div>
            {Number(profitPct) > 0 && (
              <span className="rfp-hours-manual-tag">of&nbsp;{fmt(baseCost, currSymbol)}</span>
            )}
          </div>
          <div className="rfp-charge-row-value">
            {Number(profitPct) > 0
              ? <span>+{fmt(profitAmount, currSymbol)}</span>
              : <em className="text-muted" style={{ fontSize: "12px" }}>—</em>}
          </div>
        </div>

        {/* COMMISSION */}
        <div className="rfp-hours-row rfp-charge-row" style={{ gap: "40px" }}>
          <div className="rfp-cost-col-role rfp-hours-row-label">Commission</div>
          <div className="rfp-charge-row-controls" style={{ gridColumn: "3 / 5" }}>
            <Select
              options={staffOptions}
              value={commissionStaff}
              onChange={setCommissionStaff}
              styles={makeSelectStyles("160px")}
              placeholder="Select staff…"
              isDisabled={isLoading || readOnly}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              isClearable
            />
            <div className="rfp-pct-wrap">
              <Input
                type="number" min={0} max={100} placeholder="0"
                value={commissionPct}
                onChange={(e) => !readOnly && setCommissionPct(e.target.value)}
                onKeyPress={(e) => { if (!/[0-9.]/.test(e.key)) e.preventDefault(); }}
                disabled={readOnly}
                className="rfp-pct-input"
              />
              <span className="rfp-pct-symbol">%</span>
            </div>
            {Number(commissionPct) > 0 && (
              <span className="rfp-hours-manual-tag">of&nbsp;{fmt(afterProfit, currSymbol)}</span>
            )}
          </div>
          <div className="rfp-charge-row-value">
            {Number(commissionPct) > 0
              ? <span>+{fmt(commissionAmount, currSymbol)}</span>
              : <em className="text-muted" style={{ fontSize: "12px" }}>—</em>}
          </div>
        </div>

        {/* GO CARDLESS (conditional) */}
        {/* {goCardlessEnabled && (
          <div className="rfp-hours-row rfp-charge-row rfp-charge-row--gc" style={{ gap: "40px" }}>
            <div className="rfp-cost-col-role rfp-hours-row-label">Go Cardless</div>
            <div className="rfp-charge-row-controls" style={{ gridColumn: "3 / 5" }}>
              <div className="rfp-pct-wrap">
                <Input
                  type="number" min={0} max={100} placeholder="0"
                  value={goCardlessPct}
                  onChange={(e) => !readOnly && setGoCardlessPct(e.target.value)}
                  onKeyPress={(e) => { if (!/[0-9.]/.test(e.key)) e.preventDefault(); }}
                  disabled={readOnly}
                  className="rfp-pct-input"
                />
                <span className="rfp-pct-symbol">%</span>
              </div>
              {Number(goCardlessPct) > 0 && (
                <span className="rfp-hours-manual-tag">of&nbsp;{fmt(afterCommission, currSymbol)}</span>
              )}
            </div>
            <div className="rfp-charge-row-value">
              {Number(goCardlessPct) > 0
                ? <span>+{fmt(goCardlessAmount, currSymbol)}</span>
                : <em className="text-muted" style={{ fontSize: "12px" }}>—</em>}
            </div>
          </div>
        )} */}

        {/* SUB TOTAL with GoCardless toggle */}
        {/* <div className="rfp-hours-row rfp-subtotal-row">
          <span className="rfp-cost-col-role fw-semibold">Sub Total</span>
          <div className="rfp-subtotal-right">
            <div className="rfp-gc-toggle-wrap">
              <button
                type="button"
                className={`rfp-gc-toggle ${goCardlessEnabled ? "rfp-gc-toggle--on" : ""}`}
                onClick={() => !readOnly && setGoCardlessEnabled(p => !p)}
                disabled={readOnly}
              >
                <span className="rfp-gc-knob" />
              </button>
              <span className="rfp-gc-label-text">Go Cardless</span>
              {goCardlessEnabled
                ? <span className="rfp-gc-badge rfp-gc-badge--on">ON</span>
                : <span className="rfp-gc-badge rfp-gc-badge--off">OFF</span>}
            </div>
            <span className="rfp-subtotal-value">{fmt(grandTotal, currSymbol)}</span>
          </div>
        </div> */}
        {/* SUB TOTAL with GoCardless toggle */}
        {/* <div className="rfp-hours-row rfp-subtotal-row">
  <span className="rfp-cost-col-role fw-semibold">Sub Total</span>
  <div className="rfp-subtotal-right">
    <div className="rfp-gc-toggle-wrap">
      <button
        type="button"
        className={`rfp-gc-toggle ${goCardlessEnabled ? "rfp-gc-toggle--on" : ""}`}
        onClick={() => !readOnly && setGoCardlessEnabled(p => !p)}
        disabled={readOnly}
      >
        <span className="rfp-gc-knob" />
      </button>
      <span className="rfp-gc-label-text">Go Cardless</span>
      {goCardlessEnabled
        ? <span className="rfp-gc-badge rfp-gc-badge--on">ON</span>
        : <span className="rfp-gc-badge rfp-gc-badge--off">OFF</span>}
    </div>
    <span className="rfp-subtotal-value">{fmt(subTotal, currSymbol)}</span>
  </div>
</div> */}

        {/* GST row */}
        {/* <div className="rfp-hours-row rfp-charge-row" style={{ gap: "40px" }}>
  <div className="rfp-cost-col-role rfp-hours-row-label">GST</div>
  <div className="rfp-charge-row-controls" style={{ gridColumn: "3 / 5" }}>
    <div className="rfp-pct-wrap">
      <Input
        type="number" min={0} max={100} placeholder="18"
        value={gstPct}
        onChange={(e) => !readOnly && setGstPct(e.target.value)}
        onKeyPress={(e) => { if (!/[0-9.]/.test(e.key)) e.preventDefault(); }}
        disabled={readOnly}
        className="rfp-pct-input"
      />
      <span className="rfp-pct-symbol">%</span>
    </div>
    {Number(gstPct) > 0 && (
      <span className="rfp-hours-manual-tag">of&nbsp;{fmt(subTotal, currSymbol)}</span>
    )}
  </div>
  <div className="rfp-charge-row-value">
    {Number(gstPct) > 0
      ? <span>+{fmt(gstAmount, currSymbol)}</span>
      : <em className="text-muted" style={{ fontSize: "12px" }}>—</em>}
  </div>
</div> */}

        {/* GRAND TOTAL */}
        {/* <div className="rfp-total-bar rfp-grand-total-bar">
  <div className="d-flex flex-column gap-1">
    <span className="rfp-total-label">Grand Total</span>
    <span className="rfp-grand-total-note">
      {fmt(subTotal, currSymbol)}&nbsp;+&nbsp;GST&nbsp;{fmt(gstAmount, currSymbol)}
    </span>
  </div>
  <div className="d-flex flex-column align-items-end gap-1">
    <span className="rfp-total-value">{fmt(grandTotal, currSymbol)}</span>
    <span className="rfp-grand-total-note">Incl.&nbsp;{Number(gstPct) || 0}%&nbsp;GST</span>
  </div>
</div> */}

        {/* SUB TOTAL with GST toggle */}
        <div className="rfp-hours-row rfp-subtotal-row">
          <span className="rfp-cost-col-role fw-semibold">Sub Total</span>
          <div className="rfp-subtotal-right">
            <div className="rfp-gc-toggle-wrap">
              <button
                type="button"
                className={`rfp-gc-toggle ${goCardlessEnabled ? "rfp-gc-toggle--on" : ""}`}
                onClick={() => {
                  if (readOnly) return;
                  const turningOn = !goCardlessEnabled;
                  setGoCardlessEnabled(turningOn);
                  if (turningOn) {
                    setGstPct(18);
                  } else {
                    setGstPct("");
                  }
                }}
                disabled={readOnly}
              >
                <span className="rfp-gc-knob" />
              </button>
              <span className="rfp-gc-label-text">GST</span>
              {goCardlessEnabled
                ? <span className="rfp-gc-badge rfp-gc-badge--on">ON</span>
                : <span className="rfp-gc-badge rfp-gc-badge--off">OFF</span>}
            </div>
            <span className="rfp-subtotal-value">{fmt(subTotal, currSymbol)}</span>
          </div>
        </div>

        {/* GST conditional row — shown only when enabled */}
        {goCardlessEnabled && (
          <div className="rfp-hours-row rfp-charge-row rfp-charge-row--gc" style={{ gap: "40px" }}>
            <div className="rfp-cost-col-role rfp-hours-row-label">GST</div>
            <div className="rfp-charge-row-controls" style={{ gridColumn: "3 / 5" }}>
              <div className="rfp-pct-wrap">
                <Input
                  type="number" min={0} max={100} placeholder="18"
                  value={gstPct}
                  onChange={(e) => !readOnly && setGstPct(e.target.value)}
                  onKeyPress={(e) => { if (!/[0-9.]/.test(e.key)) e.preventDefault(); }}
                  disabled={readOnly}
                  className="rfp-pct-input"
                />
                <span className="rfp-pct-symbol">%</span>
              </div>
              {Number(gstPct) > 0 && (
                <span className="rfp-hours-manual-tag">of&nbsp;{fmt(subTotal, currSymbol)}</span>
              )}
            </div>
            <div className="rfp-charge-row-value">
              {Number(gstPct) > 0
                ? <span>+{fmt(gstAmount, currSymbol)}</span>
                : <em className="text-muted" style={{ fontSize: "12px" }}>—</em>}
            </div>
          </div>
        )}

        {/* GRAND TOTAL */}
        <div className="rfp-total-bar rfp-grand-total-bar">
          <div className="d-flex flex-column gap-1">
            <span className="rfp-total-label">Grand Total</span>
            <span className="rfp-grand-total-note">
              {fmt(subTotal, currSymbol)}&nbsp;+&nbsp;GST&nbsp;{fmt(gstAmount, currSymbol)}
            </span>
          </div>
          <div className="d-flex flex-column align-items-end gap-1">
            <span className="rfp-total-value">{fmt(grandTotal, currSymbol)}</span>
            <span className="rfp-grand-total-note">
              Incl.&nbsp;{goCardlessEnabled ? (Number(gstPct) || 0) : 0}%&nbsp;GST
            </span>
          </div>
        </div>

      </div>

      {/* ── ACTIVITY LOG (keep your existing block here) ── */}
      {stepLogs && (
        <div className="rfp-activity-log mt-4">
          <div className="rfp-activity-log-title">Activity</div>
          <div className="d-flex gap-3 flex-wrap">
            {(() => {
              const cards = [
                { label: "Created By", sublabel: null, entry: stepLogs.step2?.saved?.[0], active: !!stepLogs.step2?.saved?.[0] },
                { label: "Level 1", sublabel: "(Timing)", entry: stepLogs.step3?.submitted?.at(-1), active: !!stepLogs.step3?.submitted?.at(-1) },
                { label: "Level 2", sublabel: "(Costing)", entry: stepLogs.step4?.saved?.at(-1), active: !!stepLogs.step4?.saved?.at(-1) && !!stepLogs.step3?.approved?.at(-1) },
                { label: "Final Approval", sublabel: "(Overview)", entry: stepLogs.step5?.approved?.at(-1), active: !!stepLogs.step5?.approved?.at(-1) && !!stepLogs.step4?.saved?.at(-1) && !!stepLogs.step3?.approved?.at(-1) },
              ];
              const visibleCards = [];
              for (const card of cards) { if (!card.active) break; visibleCards.push(card); }
              return visibleCards.map((card, i) => {
                const user = allUsers?.find(u => u.id == card.entry.by);
                return (
                  <div key={i} className="rfp-activity-pill">
                    <div className="rfp-activity-icon">
                      {user?.avatar
                        ? <img src={`${configure.imgUrl}${user.avatar}`} alt={user.name} style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />
                        : <i className="fas fa-user" />}
                    </div>
                    <div>
                      <div className="rfp-activity-label">
                        {card.label}{" "}
                        {card.sublabel && (<span style={{ fontWeight: 400, textTransform: "none" }}>{card.sublabel}</span>)}
                      </div>
                      <div className="rfp-activity-user">{user?.name || `User #${card.entry.by}`}</div>
                      <div className="rfp-activity-time">{new Date(card.entry.at).toLocaleString()}</div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepCosting;